"use client";

import { useCallback, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Box,
  Button,
  Field,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  TARGET_URL,
  isMcpConsentAuthorizationTarget,
  normalizeSafeInternalTarget,
} from "@/app/(public)/(auth)/authRedirectUtils";
import authSettings from "@/shared/config/authSettings";
import type { GenericDrawerInfoItem } from "@/shared/ui/display/GenericDrawerInfo";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import ProfilePerspectivesMotif from "@/shared/ui/display/ProfilePerspectivesMotif";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import { useUserProfile } from "@/state/globalStore";
import type { SupportedLanguage } from "@/generated/fastapi";
import usePrintSettingsActions from "../hooks/usePrintSettingsActions";
import UploadResumeForm from "./UploadResumeForm";

type SetupSectionProps = {
  children: React.ReactNode;
  description?: string;
  emphasis?: "default" | "primary";
  title: string;
};

function SetupSection({
  children,
  description,
  emphasis = "default",
  title,
}: SetupSectionProps) {
  const isPrimary = emphasis === "primary";
  return (
    <Box
      w="full"
      bg="moveon.surface.bg"
      color="moveon.surface.fg"
      borderWidth="1"
      borderColor={isPrimary ? "moveon.primaryLight" : "moveon.surface.border"}
      borderTopWidth={isPrimary ? "3" : "1"}
      borderTopColor={isPrimary ? "moveon.primary" : undefined}
      shadow="sm"
      borderRadius="md"
      p={{ base: "3", sm: "4", md: "5" }}
    >
      <Stack gap="4" align="stretch">
        <Stack gap="1" align="start">
          <Heading as="h2" size="sm">
            {title}
          </Heading>
          {description ? (
            <Text color="moveon.text.muted" fontSize="sm" lineHeight="tall">
              {description}
            </Text>
          ) : null}
        </Stack>
        {children}
      </Stack>
    </Box>
  );
}

function SetupHeader() {
  return (
    <Stack gap="4" align="start">
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        boxSize="11"
        borderRadius="full"
        bg="white"
        color="moveon.primary"
        borderWidth="1"
        borderColor="moveon.surface.border"
        shadow="sm"
      >
        <GenericIcon icon="profile" size="md" />
      </Box>
      <Stack gap="2" align="start">
        <Heading as="h1" size={{ base: "lg", md: "xl" }}>
          Profile Yourself
        </Heading>
        <Text
          color="moveon.text.muted"
          fontSize={{ base: "sm", md: "md" }}
          lineHeight="tall"
          maxW="2xl"
        >
          Choose the fastest starting point. Import a CV into Backlog as source
          material, or continue to Studio and shape career situations manually.
        </Text>
      </Stack>
    </Stack>
  );
}

function ConsentNotice() {
  return (
    <Box
      width="full"
      borderWidth="1"
      borderColor="moveon.primaryLight"
      bg="moveon.primaryLight"
      borderRadius="md"
      px={{ base: "3", md: "4" }}
      py="3"
    >
      <HStack gap="3" align="start">
        <Box color="moveon.primary" pt="0.5">
          <GenericIcon icon="lock" size="sm" />
        </Box>
        <Stack gap="0.5">
          <Heading as="h2" size="xs">
            Authorization will continue after setup
          </Heading>
          <Text color="moveon.surface.fg" fontSize="sm" lineHeight="tall">
            You can import source material into Backlog first, or skip this
            setup step and return to the authorization flow.
          </Text>
        </Stack>
      </HStack>
    </Box>
  );
}

type PreferredLanguageSectionProps = {
  disabled: boolean;
  isUpdating: boolean;
  languageItems: ReturnType<typeof useOptionItems>["languageItems"];
  onChange: (selectedLanguages: SupportedLanguage[]) => void;
  preferredLanguage: SupportedLanguage | null;
  preferredLanguageLabel: string;
};

function PreferredLanguageSection({
  disabled,
  isUpdating,
  languageItems,
  onChange,
  preferredLanguage,
  preferredLanguageLabel,
}: PreferredLanguageSectionProps) {
  return (
    <SetupSection
      title="Print language"
      description="Used for profile exports and as a fallback when source material language is unclear."
    >
      <Stack gap="3" align="stretch">
        <HStack
          gap="2"
          justify="space-between"
          align={{ base: "start", sm: "center" }}
          flexDirection={{ base: "column", sm: "row" }}
        >
          <Text color="moveon.text.muted" fontSize="sm">
            Current selection
          </Text>
          <Box
            px="2.5"
            py="1"
            borderRadius="full"
            bg={
              preferredLanguage ? "moveon.primaryLight" : "moveon.warningLight"
            }
            color={preferredLanguage ? "moveon.primary" : "moveon.warning"}
            borderWidth="1"
            borderColor={
              preferredLanguage ? "moveon.primaryLight" : "moveon.warningLight"
            }
            fontSize="sm"
            fontWeight="medium"
          >
            {preferredLanguage ? (
              <HStack gap="1">
                <GenericIcon
                  icon={preferredLanguage}
                  size="xs"
                  humanReadable={`${preferredLanguageLabel} flag`}
                />
                <Text as="span">{preferredLanguageLabel}</Text>
              </HStack>
            ) : (
              "None"
            )}
          </Box>
        </HStack>

        <Field.Root>
          <GenericSelector<SupportedLanguage>
            previewItems={languageItems}
            stateObject={preferredLanguage ? [preferredLanguage] : []}
            stateObjectSetter={onChange}
            mode="select"
            placeholder="Select preferred language"
            disableClear={true}
            disabled={disabled || isUpdating}
            width="full"
            minWidthItemList="full"
          />
        </Field.Root>
      </Stack>
    </SetupSection>
  );
}

type ManualStartSectionProps = {
  actionDescription: string;
  actionItems: GenericDrawerInfoItem[];
  actionLabel: string;
  isMcpConsentFlow: boolean;
  onContinue: () => void;
};

function ManualStartSection({
  actionDescription,
  actionItems,
  actionLabel,
  isMcpConsentFlow,
  onContinue,
}: ManualStartSectionProps) {
  return (
    <SetupSection
      title={isMcpConsentFlow ? "Skip setup" : "Continue in Studio"}
      description={actionDescription}
    >
      <GenericDrawerInfo
        textAlign="left"
        description={
          isMcpConsentFlow
            ? "Best when authorization is the immediate task."
            : "Best when you want to start with a specific career moment."
        }
        items={actionItems}
      />
      <Button
        alignSelf="flex-end"
        bg="moveon.primary"
        color="white"
        _hover={{ bg: "moveon.primaryLight", color: "moveon.primary" }}
        onClick={onContinue}
        aria-label={
          isMcpConsentFlow
            ? "Continue authorization"
            : "Start with manual profile shaping"
        }
      >
        <Text>{actionLabel}</Text>
        <GenericIcon icon="arrow_right" />
      </Button>
    </SetupSection>
  );
}

function ResumeStartSection({
  onSuccessParsing,
  preferredLanguage,
}: {
  onSuccessParsing: () => void;
  preferredLanguage: SupportedLanguage | null;
}) {
  return (
    <SetupSection
      title="Start from existing CV"
      description="Fastest when a CV can seed Backlog with source material and help Studio create career situations, achievements, and aspirations."
      emphasis="primary"
    >
      <UploadResumeForm
        preferredLanguage={preferredLanguage}
        onSuccessParsing={onSuccessParsing}
      />
    </SetupSection>
  );
}

function ResumeStartSplashSection({
  onSuccessParsing,
  preferredLanguage,
}: {
  onSuccessParsing: () => void;
  preferredLanguage: SupportedLanguage | null;
}) {
  return (
    <Box position="relative" isolation="isolate" py={{ base: "5", md: "7" }}>
      <Box
        position="absolute"
        inset="50% auto auto 50%"
        transform="translate(-50%, -50%)"
        zIndex="0"
        aria-hidden="true"
        pointerEvents="none"
      >
        <ProfilePerspectivesMotif variant="splash" opacity="0.7" />
      </Box>

      <Box position="relative" zIndex="1">
        <ResumeStartSection
          preferredLanguage={preferredLanguage}
          onSuccessParsing={onSuccessParsing}
        />
      </Box>
    </Box>
  );
}

export default function InitializeAccountChoice() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [preferredLanguage, setPreferredLanguage] =
    useState<SupportedLanguage | null>(null);
  const { userProfile } = useUserProfile();
  const { languageItems } = useOptionItems();
  const { updatePrintSettingsSync, isUpdating } = usePrintSettingsActions();
  const rawTargetUrl = searchParams.getAll(TARGET_URL).at(-1);
  const safeTargetUrl = normalizeSafeInternalTarget(rawTargetUrl);
  const targetUrl = safeTargetUrl ?? authSettings.redirects.privateHome;
  const isMcpConsentFlow = isMcpConsentAuthorizationTarget(safeTargetUrl);
  const preferredLanguageLabel =
    languageItems.find((item) => item.value === preferredLanguage)?.label ??
    "None";
  const manualActionLabel = isMcpConsentFlow
    ? "Continue authorization"
    : "Continue in Studio";
  const manualActionDescription = isMcpConsentFlow
    ? "Skip profile setup for now and continue the authorization flow."
    : "Open Studio to add a situation first, then shape confirmed thoughts around it.";
  const manualActionItems: GenericDrawerInfoItem[] = isMcpConsentFlow
    ? [
        {
          label: "Authorization",
          bg: "moveon.tertiaryLight",
          borderColor: "moveon.tertiaryLight",
          color: "moveon.tertiary",
        },
      ]
    : [
        {
          label: "Studio",
          bg: "moveon.primaryLight",
          borderColor: "moveon.primaryLight",
          color: "moveon.primary",
        },
        {
          label: "Situation-first",
          bg: "moveon.secondaryLight",
          borderColor: "moveon.secondaryLight",
          color: "moveon.secondary",
        },
      ];

  const handlePreferredLanguageChange = useCallback(
    (selectedLanguages: SupportedLanguage[]) => {
      const nextPreferredLanguage = selectedLanguages[0];
      setPreferredLanguage(nextPreferredLanguage ?? null);
      if (!nextPreferredLanguage) return;
      void updatePrintSettingsSync({ print_language: nextPreferredLanguage });
    },
    [updatePrintSettingsSync],
  );

  const continueToTarget = useCallback(() => {
    router.replace(targetUrl);
  }, [router, targetUrl]);

  return (
    <Box
      w="full"
      minH="full"
      bg="moveon.bg"
      color="moveon.surface.fg"
      px={{ base: "3", md: "6" }}
      py={{ base: "5", md: "8" }}
      overflowX="hidden"
    >
      <VStack
        w="full"
        maxW="4xl"
        mx="auto"
        align="stretch"
        gap={{ base: "16", sm: "24", md: "24" }}
      >
        <SetupHeader />
        {isMcpConsentFlow ? <ConsentNotice /> : null}

        <PreferredLanguageSection
          disabled={userProfile.isLoading}
          isUpdating={isUpdating}
          languageItems={languageItems}
          onChange={handlePreferredLanguageChange}
          preferredLanguage={preferredLanguage}
          preferredLanguageLabel={preferredLanguageLabel}
        />

        <ResumeStartSplashSection
          preferredLanguage={preferredLanguage}
          onSuccessParsing={continueToTarget}
        />

        <ManualStartSection
          actionDescription={manualActionDescription}
          actionItems={manualActionItems}
          actionLabel={manualActionLabel}
          isMcpConsentFlow={isMcpConsentFlow}
          onContinue={continueToTarget}
        />
      </VStack>
    </Box>
  );
}
